import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useEmpresaActiva } from '../../context/EmpresaContableContext';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Grid, Chip, IconButton,
  Tabs, Tab, Divider, Tooltip, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import InventoryIcon from '@mui/icons-material/Inventory2';
import DownloadIcon from '@mui/icons-material/Download';
import ExcelJS from 'exceljs';

const API = '/api/contabilidad';

async function exportarActivosExcel(empresa, activos) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'InsightSoft ERP';
  const ws = wb.addWorksheet('Activos Fijos', { pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1 } });
  const columns = [
    { header: 'Código', key: 'codigo', width: 14 },
    { header: 'Descripción', key: 'descripcion', width: 36 },
    { header: 'Categoría', key: 'categoria', width: 22 },
    { header: 'F. Adquisición', key: 'fecha', width: 14 },
    { header: 'Valor Adquisición', key: 'valorAdq', width: 18, type: 'number' },
    { header: 'Depreciación Acum.', key: 'deprAcum', width: 18, type: 'number' },
    { header: 'Valor Neto en Libros', key: 'valorNeto', width: 18, type: 'number' },
    { header: 'Vida Útil (meses)', key: 'vidaUtil', width: 14 },
    { header: 'Estado', key: 'estado', width: 14 },
  ];
  const ncols = columns.length, half = Math.ceil(ncols / 2);
  ws.mergeCells(1, 1, 1, ncols);
  const r1 = ws.getRow(1); r1.height = 22;
  const c1 = r1.getCell(1);
  c1.value = empresa?.razon_social || 'EMPRESA';
  c1.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  c1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5E' } };
  c1.alignment = { horizontal: 'center', vertical: 'middle' };

  ws.mergeCells(2, 1, 2, half);
  ws.mergeCells(2, half + 1, 2, ncols);
  const r2 = ws.getRow(2); r2.height = 18;
  const c2a = r2.getCell(1);
  c2a.value = `RUC: ${empresa?.ruc || ''}`;
  c2a.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
  c2a.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E6DA4' } };
  const c2b = r2.getCell(half + 1);
  c2b.value = 'REGISTRO DE ACTIVOS FIJOS';
  c2b.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
  c2b.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E6DA4' } };
  c2b.alignment = { horizontal: 'right', vertical: 'middle' };

  ws.mergeCells(3, 1, 3, ncols);
  const r3 = ws.getRow(3); r3.height = 16;
  const c3 = r3.getCell(1);
  c3.value = `Generado: ${new Date().toLocaleString('es-PE')} — Nota: este reporte es un auxiliar interno, no es el Registro de Activos Fijos PLE oficial de SUNAT.`;
  c3.font = { name: 'Calibri', size: 9, italic: true, color: { argb: 'FFFFFFFF' } };
  c3.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E6DA4' } };

  const r4 = ws.getRow(4); r4.height = 26;
  columns.forEach((col, i) => {
    const cell = r4.getCell(i + 1);
    cell.value = col.header;
    cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E4D8C' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  });
  ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: ncols } };
  ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }];

  let totalAdq = 0, totalDepr = 0, totalNeto = 0;
  activos.forEach((a, idx) => {
    const wr = ws.getRow(5 + idx);
    const isEven = idx % 2 === 0;
    const valorAdq = parseFloat(a.valor_adquisicion || 0);
    const deprAcum = parseFloat(a.depreciacion_acumulada || 0);
    const valorNeto = parseFloat(a.valor_neto != null ? a.valor_neto : valorAdq - deprAcum);
    totalAdq += valorAdq; totalDepr += deprAcum; totalNeto += valorNeto;
    const vals = { codigo: a.codigo, descripcion: a.descripcion, categoria: a.categoria || '—', fecha: a.fecha_adquisicion?.substring(0, 10) || '',
      valorAdq: +valorAdq.toFixed(2), deprAcum: +deprAcum.toFixed(2), valorNeto: +valorNeto.toFixed(2),
      vidaUtil: a.vida_util_meses, estado: a.estado === 'ACTIVO' ? 'Activo' : 'Dado de baja' };
    columns.forEach((col, ci) => {
      const cell = wr.getCell(ci + 1);
      cell.value = vals[col.key];
      if (col.type === 'number') { cell.numFmt = '#,##0.00'; cell.alignment = { horizontal: 'right', vertical: 'middle' }; }
      cell.font = { name: 'Calibri', size: 9 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFEDF5FF' : 'FFFFFFFF' } };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFCCE0FF' } }, right: { style: 'hair', color: { argb: 'FFCCE0FF' } } };
    });
  });

  const lastRow = 5 + activos.length;
  const tr = ws.getRow(lastRow);
  tr.getCell(1).value = 'TOTALES';
  const totMap = { valorAdq: totalAdq, deprAcum: totalDepr, valorNeto: totalNeto };
  columns.forEach((col, ci) => {
    const cell = tr.getCell(ci + 1);
    if (totMap[col.key] !== undefined) { cell.value = +totMap[col.key].toFixed(2); cell.numFmt = '#,##0.00'; cell.alignment = { horizontal: 'right' }; }
    cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF1A3A5E' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8DC' } };
    cell.border = { top: { style: 'medium', color: { argb: 'FF1E4D8C' } }, bottom: { style: 'medium', color: { argb: 'FF1E4D8C' } } };
  });

  const buf = await wb.xlsx.writeBuffer();
  const blobUrl = window.URL.createObjectURL(new Blob([buf], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = blobUrl; a.download = `ActivosFijos_${empresa?.ruc || ''}.xlsx`; a.click();
  window.URL.revokeObjectURL(blobUrl);
}
const CATEGORIAS = ['Inmuebles y Terrenos','Maquinaria y Equipo','Vehículos','Muebles y Enseres','Equipos de Cómputo','Herramientas','Intangibles','Otros'];
function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const FORM_INIT = { codigo: '', descripcion: '', categoria: '', cuenta_activo_id: '', cuenta_depreciacion_id: '', cuenta_gasto_depr_id: '', fecha_adquisicion: '', valor_adquisicion: '', valor_residual: '0', vida_util_meses: '', metodo: 'LINEAL' };

export default function ActivosFijos() {
  const [tab, setTab] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const { empresaActivaId, setEmpresaActiva } = useEmpresaActiva();
  const [empresaId, setEmpresaId] = useState(empresaActivaId ? Number(empresaActivaId) : '');
  const [activos, setActivos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // CRUD
  const [dlgForm, setDlgForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_INIT);
  const [saving, setSaving] = useState(false);

  // Baja
  const [dlgBaja, setDlgBaja] = useState(null);
  const [motivoBaja, setMotivoBaja] = useState('');

  // Depreciación
  const [periodos, setPeriodos] = useState([]);
  const [periodoDepr, setPeriodoDepr] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    cargarActivos();
    axios.get(`${API}/plan?empresa_id=${empresaId}`).then(r => setCuentas(r.data)).catch(() => {});
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      const abiertos = r.data.filter(p => p.estado === 'ABIERTO').sort((a,b) => b.periodo.localeCompare(a.periodo));
      setPeriodos(r.data);
      if (abiertos.length > 0) setPeriodoDepr(abiertos[0].periodo);
    }).catch(() => {});
  }, [empresaId]);

  const cargarActivos = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/activos-fijos?empresa_id=${empresaId}`);
      setActivos(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setLoading(false);
  }, [empresaId]);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function abrirNuevo() { setEditando(null); setForm(FORM_INIT); setDlgForm(true); }
  function abrirEditar(a) {
    setEditando(a);
    setForm({ codigo: a.codigo, descripcion: a.descripcion, categoria: a.categoria || '', cuenta_activo_id: a.cuenta_activo_id, cuenta_depreciacion_id: a.cuenta_depreciacion_id, cuenta_gasto_depr_id: a.cuenta_gasto_depr_id, fecha_adquisicion: a.fecha_adquisicion?.substring(0,10), valor_adquisicion: a.valor_adquisicion, valor_residual: a.valor_residual, vida_util_meses: a.vida_util_meses, metodo: a.metodo });
    setDlgForm(true);
  }

  async function guardar() {
    if (!form.codigo || !form.descripcion || !form.cuenta_activo_id || !form.cuenta_depreciacion_id || !form.cuenta_gasto_depr_id || !form.fecha_adquisicion || !form.valor_adquisicion || !form.vida_util_meses)
      return setError('Completa todos los campos requeridos');
    setSaving(true);
    try {
      if (editando) {
        await axios.put(`${API}/activos-fijos/${editando.id}`, form);
        setSuccess('Activo actualizado');
      } else {
        await axios.post(`${API}/activos-fijos`, { ...form, empresa_id: empresaId });
        setSuccess('Activo registrado');
      }
      setDlgForm(false); cargarActivos();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setSaving(false);
  }

  async function darBaja() {
    if (!dlgBaja) return;
    try {
      await axios.put(`${API}/activos-fijos/${dlgBaja.id}/baja`, { motivo_baja: motivoBaja, fecha_baja: new Date().toISOString().substring(0,10) });
      setSuccess('Activo dado de baja'); setDlgBaja(null); setMotivoBaja(''); cargarActivos();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  async function verPreview() {
    if (!empresaId || !periodoDepr) return setError('Selecciona empresa y período');
    setLoadingPreview(true); setError(''); setPreview(null);
    try {
      const r = await axios.get(`${API}/activos-fijos/depreciacion/preview?empresa_id=${empresaId}&periodo=${periodoDepr}`);
      setPreview(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setLoadingPreview(false);
  }

  async function ejecutarDepr() {
    if (!window.confirm(`¿Ejecutar depreciación del período ${periodoDepr}? Se generará un asiento contable.`)) return;
    setEjecutando(true);
    try {
      const r = await axios.post(`${API}/activos-fijos/depreciacion/ejecutar`, { empresa_id: empresaId, periodo: periodoDepr });
      setSuccess(`Depreciación ejecutada: ${r.data.procesados} activos, S/ ${fmt(r.data.total_depreciacion)}`);
      setPreview(null); cargarActivos();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setEjecutando(false);
  }

  const cuentas33 = cuentas.filter(c => c.codigo?.startsWith('33'));
  const cuentas39 = cuentas.filter(c => c.codigo?.startsWith('39'));
  const cuentas68 = cuentas.filter(c => c.codigo?.startsWith('68'));

  const activosActivos = activos.filter(a => a.estado === 'ACTIVO');
  const activosBaja    = activos.filter(a => a.estado === 'DADO_DE_BAJA');
  const totalValor     = activosActivos.reduce((s, a) => s + parseFloat(a.valor_adquisicion || 0), 0);
  const totalDeprAcum  = activosActivos.reduce((s, a) => s + parseFloat(a.depreciacion_acumulada || 0), 0);
  const totalNeto      = totalValor - totalDeprAcum;

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <InventoryIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Activos Fijos</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Registro de Activos" />
        <Tab label="Depreciación Mensual" icon={<PlayArrowIcon />} iconPosition="start" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ── TAB 0: REGISTRO ── */}
      {tab === 0 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => { setEmpresaId(e.target.value); setEmpresaActiva(e.target.value); }} sx={{ minWidth: '24em' }}>
                  {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo} disabled={!empresaId}>Nuevo Activo</Button>
                  <IconButton onClick={cargarActivos} disabled={!empresaId || loading}><RefreshIcon /></IconButton>
                  {activos.length > 0 && (
                    <Button variant="outlined" color="success" startIcon={<DownloadIcon />}
                      onClick={() => exportarActivosExcel(empresas.find(e => e.id === empresaId), activos)}>
                      Excel
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* KPIs */}
          {activosActivos.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[
                { label: 'Activos en servicio', value: activosActivos.length, unit: 'activos', color: 'primary.main' },
                { label: 'Valor de adquisición', value: `S/ ${fmt(totalValor)}`, color: 'text.primary' },
                { label: 'Depreciación acumulada', value: `S/ ${fmt(totalDeprAcum)}`, color: 'error.main' },
                { label: 'Valor neto en libros', value: `S/ ${fmt(totalNeto)}`, color: 'success.main' },
              ].map((k, i) => (
                <Grid key={i} item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                    <Typography fontWeight="bold" color={k.color}>{k.value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    {['Código','Descripción','Categoría','F. Adquisición','Valor Adq.','Depr. Acum.','Valor Neto','Vida Útil','Estado','Acc.'].map(h => (
                      <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activos.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay activos registrados</TableCell></TableRow>
                  ) : activos.map(a => {
                    const pct = parseFloat(a.valor_depreciable) > 0 ? (parseFloat(a.depreciacion_acumulada) / parseFloat(a.valor_depreciable)) * 100 : 0;
                    return (
                      <TableRow key={a.id} hover sx={{ opacity: a.estado === 'DADO_DE_BAJA' ? 0.6 : 1 }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.codigo}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>{a.descripcion}</TableCell>
                        <TableCell>{a.categoria || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{a.fecha_adquisicion?.substring(0,10)}</TableCell>
                        <TableCell align="right">S/ {fmt(a.valor_adquisicion)}</TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography variant="caption">S/ {fmt(a.depreciacion_acumulada)}</Typography>
                            <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{ mt: 0.3, height: 4, borderRadius: 2 }} color={pct >= 100 ? 'error' : 'primary'} />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>S/ {fmt(a.valor_neto)}</TableCell>
                        <TableCell align="center">{a.vida_util_meses} m.</TableCell>
                        <TableCell align="center">
                          <Chip label={a.estado === 'ACTIVO' ? 'Activo' : 'Baja'} size="small" color={a.estado === 'ACTIVO' ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          {a.estado === 'ACTIVO' && <Tooltip title="Dar de baja"><IconButton size="small" color="error" onClick={() => setDlgBaja(a)}><BlockIcon fontSize="small" /></IconButton></Tooltip>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── TAB 1: DEPRECIACIÓN ── */}
      {tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => { setEmpresaId(e.target.value); setEmpresaActiva(e.target.value); }} sx={{ minWidth: '24em' }}>
                  {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Período" value={periodoDepr} onChange={e => { setPeriodoDepr(e.target.value); setPreview(null); }} disabled={!empresaId} sx={{ minWidth: '20em' }}>
                  {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={verPreview} disabled={!empresaId || !periodoDepr || loadingPreview}>
                    {loadingPreview ? <CircularProgress size={18} /> : 'Vista Previa'}
                  </Button>
                  {preview && preview.pendientes_count > 0 && (
                    <Button variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={ejecutarDepr} disabled={ejecutando}>
                      {ejecutando ? <CircularProgress size={18} color="inherit" /> : `Ejecutar (${preview.pendientes_count} activos)`}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {preview && (
            <Box>
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: preview.pendientes_count > 0 ? '#e8f5e9' : '#fff3e0' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Período {preview.periodo} — {preview.pendientes_count > 0 ? `${preview.pendientes_count} activos a depreciar` : 'Sin activos pendientes'}
                </Typography>
                {preview.pendientes_count > 0 && <Typography>Total depreciación del mes: <strong>S/ {fmt(preview.total)}</strong></Typography>}
              </Paper>

              <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Código</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Cta. Gasto (D)</TableCell>
                      <TableCell>Cta. Depr. Ac. (C)</TableCell>
                      <TableCell align="right">Val. Adq.</TableCell>
                      <TableCell align="right">Depr. Acum.</TableCell>
                      <TableCell align="right">Cuota Mes</TableCell>
                      <TableCell align="right">Val. Neto tras</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.lineas.map(l => (
                      <TableRow key={l.activo_id} hover sx={{ opacity: l.ya_depreciado ? 0.5 : 1 }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{l.codigo}</TableCell>
                        <TableCell>{l.descripcion}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{l.cuenta_gasto_codigo}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{l.cuenta_depr_codigo}</TableCell>
                        <TableCell align="right">S/ {fmt(l.valor_adquisicion)}</TableCell>
                        <TableCell align="right">S/ {fmt(l.depreciacion_acumulada)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>S/ {fmt(l.importe_depr)}</TableCell>
                        <TableCell align="right">S/ {fmt(l.valor_neto)}</TableCell>
                        <TableCell align="center">
                          <Chip label={l.ya_depreciado ? 'Ya depreciado' : l.importe_depr > 0 ? 'Pendiente' : 'Totalmente dep.'} size="small"
                            color={l.ya_depreciado ? 'default' : l.importe_depr > 0 ? 'warning' : 'error'} />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold' }}>Total depreciación período:</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>S/ {fmt(preview.total)}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}

      {/* Dialog CRUD Activo */}
      <Dialog open={dlgForm} onClose={() => setDlgForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editando ? 'Editar Activo Fijo' : 'Nuevo Activo Fijo'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={3}><TextField fullWidth size="small" label="Código *" value={form.codigo} onChange={e => setF('codigo', e.target.value.toUpperCase())} disabled={!!editando} /></Grid>
            <Grid item xs={9}><TextField fullWidth size="small" label="Descripción *" value={form.descripcion} onChange={e => setF('descripcion', e.target.value)} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Categoría" value={form.categoria} onChange={e => setF('categoria', e.target.value)}>
                <MenuItem value="">Sin categoría</MenuItem>
                {CATEGORIAS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Fecha Adquisición *" type="date" InputLabelProps={{ shrink: true }} value={form.fecha_adquisicion} onChange={e => setF('fecha_adquisicion', e.target.value)} disabled={!!editando} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Valor Adquisición S/ *" type="number" value={form.valor_adquisicion} onChange={e => setF('valor_adquisicion', e.target.value)} disabled={!!editando} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Valor Residual S/" type="number" value={form.valor_residual} onChange={e => setF('valor_residual', e.target.value)} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Vida Útil (meses) *" type="number" value={form.vida_util_meses} onChange={e => setF('vida_util_meses', e.target.value)} disabled={!!editando} /></Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Cuentas Contables</Typography></Divider></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Cuenta Activo 33x *" value={form.cuenta_activo_id} onChange={e => setF('cuenta_activo_id', e.target.value)}>
                {cuentas33.map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Depr. Acumulada 39x *" value={form.cuenta_depreciacion_id} onChange={e => setF('cuenta_depreciacion_id', e.target.value)}>
                {cuentas39.map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Gasto Depreciación 68x *" value={form.cuenta_gasto_depr_id} onChange={e => setF('cuenta_gasto_depr_id', e.target.value)}>
                {cuentas68.map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editando ? 'Actualizar' : 'Registrar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Baja */}
      <Dialog open={!!dlgBaja} onClose={() => setDlgBaja(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Dar de Baja Activo</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Esta acción no se puede deshacer. El activo quedará marcado como DADO DE BAJA.</Alert>
          <Typography variant="body2" sx={{ mb: 2 }}><strong>{dlgBaja?.codigo}</strong> — {dlgBaja?.descripcion}</Typography>
          <TextField fullWidth size="small" label="Motivo de baja" multiline rows={2} value={motivoBaja} onChange={e => setMotivoBaja(e.target.value)} placeholder="Venta, robo, obsolescencia, siniestro..." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgBaja(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={darBaja}>Confirmar Baja</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

